import { useState } from 'react'; // Removed unused 'useEffect'
import './common.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import config from '../config';
import { sanitizeTextInput } from '../sanitize';

function MissingLetters({ onNavigate }) {
  // Form state
  const [formData, setFormData] = useState({
    grade: '',
    difficultyLevel: 'medium',
    maxWordLength: 6,
    context: '',
    numberOfWords: 5,
    language: 'English',
    hints: true
  });

  // Quiz state
  const [words, setWords] = useState([]);
  const [userAnswers, setUserAnswers] = useState([]);
  const [showQuiz, setShowQuiz] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState({});
  const [elapsedTime, setElapsedTime] = useState(0);
  const [timerInterval, setTimerInterval] = useState(null);
  const [showInstructions, setShowInstructions] = useState(false);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const getEmptyPercentage = () => {
    switch (formData.difficultyLevel) {
      case 'easy': return 0.1;
      case 'medium': return 0.3;
      case 'hard': return 0.9;
      default: return 0.3;
    }
  };

  const createWordPattern = (word) => {
    const emptyPercentage = getEmptyPercentage();
    const totalLetters = word.length;
    const emptyCount = Math.max(1, Math.floor(totalLetters * emptyPercentage));
    
    const pattern = word.split('').map((letter, index) => ({
      letter: letter,
      isEmpty: false,
      userInput: ''
    }));

    // Randomly select positions to make empty
    const positions = [];
    while (positions.length < emptyCount) {
      const pos = Math.floor(Math.random() * totalLetters);
      if (!positions.includes(pos)) {
        positions.push(pos);
      }
    }

    positions.forEach(pos => {
      pattern[pos].isEmpty = true;
    });

    return pattern;
  };

  const startQuiz = async () => {
    if (!formData.context.trim()) {
      setError('Please enter a context for the words');
      return;
    }

    let age = null;
    if (formData.grade !== '') {
      if (isNaN(Number(formData.grade)) || !Number.isInteger(Number(formData.grade))) {
        setError('Please enter a valid age (numbers only)');
        return;
      }
      age = parseInt(formData.grade);
      if (age < 5 || age > 18) {
        setError('Please enter an age between 5 and 18');
        return;
      }
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(config.endpoints.spellingQuiz, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          age: age !== null ? age : 8,
          difficultyLevel: formData.difficultyLevel,
          maxWordLength: parseInt(formData.maxWordLength),
          context: sanitizeTextInput(formData.context),
          numberOfWords: parseInt(formData.numberOfWords),
          language: formData.language,
          hints: formData.hints
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to generate words: ${errorText}`);
      }

      const data = await response.json();
      const wordsWithPatterns = data.map(wordData => ({
        ...wordData,
        pattern: createWordPattern(wordData.word)
      }));
      
      setWords(wordsWithPatterns);
      setUserAnswers(wordsWithPatterns.map(w => w.pattern));
      setShowQuiz(true);
      setFeedback({});
      
      // Start timer
      const now = Date.now();
      setElapsedTime(0);
      const interval = setInterval(() => {
        setElapsedTime(Date.now() - now);
      }, 1000);
      setTimerInterval(interval);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const speakWord = (wordIndex) => {
    if ('speechSynthesis' in window && words[wordIndex]) {
      const currentWord = words[wordIndex];
      const wordToSpeak = currentWord.word;
      
      // Cancel any ongoing speech
      window.speechSynthesis.cancel();
      
      const utterance = new SpeechSynthesisUtterance(wordToSpeak);
      
      // Detect if word contains Tamil characters
      const isTamil = /[\u0B80-\u0BFF]/.test(wordToSpeak);
      
      const voices = window.speechSynthesis.getVoices();
      if (isTamil || currentWord.language === 'Tamil') {
        utterance.lang = 'ta-IN';
        const tamilVoice =
          voices.find(v => (v.lang.startsWith('ta') || v.lang === 'ta-IN') && /female|woman/i.test(v.name)) ||
          voices.find(v => v.lang.startsWith('ta') || v.lang === 'ta-IN');
        if (tamilVoice) utterance.voice = tamilVoice;
      } else {
        utterance.lang = 'en-US';
        const femaleVoice =
          voices.find(v => v.lang.startsWith('en') && /zira|samantha|victoria|karen|moira|fiona|veena|female|woman/i.test(v.name)) ||
          voices.find(v => v.lang.startsWith('en') && v.name.toLowerCase().includes('female'));
        if (femaleVoice) utterance.voice = femaleVoice;
      }

      utterance.rate = 0.8;
      utterance.pitch = 4.0;
      utterance.volume = 1.0;
      
      window.speechSynthesis.speak(utterance);
    }
  };

  const handleLetterChange = (wordIndex, letterIndex, value) => {
    const newAnswers = [...userAnswers];
    if (value.length <= 1) {
      newAnswers[wordIndex][letterIndex].userInput = value.toLowerCase();
      setUserAnswers(newAnswers);
    }
  };

  const checkAnswer = (wordIndex) => {
    const userPattern = userAnswers[wordIndex]; // Removed unused 'word'

    let isCorrect = true;
    userPattern.forEach((letter) => {
      if (letter.isEmpty && letter.userInput !== letter.letter.toLowerCase()) {
        isCorrect = false;
      }
    });

    setFeedback(prev => ({
      ...prev,
      [wordIndex]: isCorrect ? 'correct' : 'incorrect'
    }));

    setTimeout(() => {
      setFeedback(prev => {
        const newFeedback = { ...prev };
        delete newFeedback[wordIndex];
        return newFeedback;
      });
    }, 2000);
  };

  const helpMe = (wordIndex) => {
    const newAnswers = [...userAnswers];
    newAnswers[wordIndex].forEach((letter, index) => {
      if (letter.isEmpty) {
        letter.userInput = letter.letter.toLowerCase();
      }
    });
    setUserAnswers(newAnswers);
  };

  const validateAll = () => {
    words.forEach((_, index) => checkAnswer(index));
  };

  const calculateScorePercentage = () => {
    const totalCorrect = words.reduce((count, wordData, wordIndex) => {
      const userPattern = userAnswers[wordIndex];
      const isCorrect = userPattern.every(
        (letter) => !letter.isEmpty || letter.userInput === letter.letter.toLowerCase()
      );
      return count + (isCorrect ? 1 : 0);
    }, 0);

    return ((totalCorrect / words.length) * 100).toFixed(2);
  };

  const stopQuiz = () => {
    // Stop timer
    if (timerInterval) {
      clearInterval(timerInterval);
      setTimerInterval(null);
    }

    // Capture duration and convert to seconds
    const durationInSeconds = Math.floor(elapsedTime / 1000);
    const score = calculateScorePercentage();

    setShowQuiz(false);
    setWords([]);
    setUserAnswers([]);
    setFeedback({});

    alert(`Quiz completed! Your score: ${score}%. Duration: ${durationInSeconds} seconds.`);
  };

  // Format elapsed time for display
  const formatTime = (milliseconds) => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  if (showQuiz && words.length > 0) {
    return (
      <div className="spelling-container">
        <Header onNavigate={onNavigate} showHomeButton={true} />
        <div className="quiz-header">
          <h1 className="quiz-title">🧩 Missing Letters Challenge</h1>
          <div className="timer-display">
            ⏱️ Time: {formatTime(elapsedTime)}
          </div>
        </div>

        <div className="quiz-card">
          <div className="instructions-box">
            <button
              className="instructions-toggle"
              onClick={() => setShowInstructions(prev => !prev)}
            >
              {showInstructions ? '🙈 Hide Instructions' : '📝 Show Instructions'}
            </button>
            {showInstructions && (
              <ol className="instructions-list">
                <li>Look at each word with missing letters (shown as empty boxes)</li>
                <li>Type the missing letters in the empty boxes</li>
                <li>Hover over the 💡 icon to see a helpful pronunciation hint</li>
                <li>Click the ✓ button to check if your answer is correct</li>
                <li>Click the 🆘 button if you need help - it will reveal the missing letters</li>
                <li>When done, click "✔️ Validate All" to check all your answers!</li>
              </ol>
            )}
          </div>

          <div className="word-counter">
            Total Words: {words.length}
          </div>

          {words.map((wordData, wordIndex) => (
            <div key={wordIndex} className="missing-letters-word-container">
              <div className="word-header">
                <span className="word-number">Word {wordIndex + 1}</span>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <button 
                    className="speaker-btn-inline"
                    onClick={() => speakWord(wordIndex)}
                    title="Click to hear the word"
                    style={{ padding: '8px 12px', fontSize: '1.2rem' }}
                  >
                    🔊
                  </button>
                  {formData.hints && wordData.phoneticHint && (
                    <div className="hint-icon-container" title={wordData.phoneticHint}>
                      <span className="hint-icon">💡</span>
                      <div className="hint-tooltip">
                        Pronunciation: {wordData.phoneticHint}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="letter-boxes-container">
                {userAnswers[wordIndex].map((letterData, letterIndex) => (
                  <div key={letterIndex} className="letter-box-wrapper">
                    {letterData.isEmpty ? (
                      <input
                        type="text"
                        className="letter-input-box"
                        value={letterData.userInput}
                        onChange={(e) => handleLetterChange(wordIndex, letterIndex, e.target.value)}
                        maxLength={1}
                        autoComplete="off"
                      />
                    ) : (
                      <div className="letter-display-box">
                        {letterData.letter}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="action-buttons">
                <button 
                  className="check-btn-inline" 
                  onClick={() => checkAnswer(wordIndex)}
                  title="Check your answer"
                >
                  ✓ Check
                </button>
                <button 
                  className="help-btn-inline" 
                  onClick={() => helpMe(wordIndex)}
                  title="Show me the answer"
                >
                  🆘 Help
                </button>
              </div>

              {feedback[wordIndex] && (
                <div className={`feedback-message ${feedback[wordIndex]}`}>
                  {feedback[wordIndex] === 'correct' 
                    ? '🎉 Perfect! You got it right!' 
                    : '❌ Not quite right. Try again!'}
                </div>
              )}
            </div>
          ))}

          <div className="validate-all-container">
            <button className="validate-all-btn" onClick={validateAll}>
              ✔️ Validate All Answers
            </button>
            <button className="stop-btn" onClick={stopQuiz}>
              ⏹ Stop Quiz
            </button>
          </div>
        </div>

        <Footer />
      </div>
    );
  }

  return (
    <div className="spelling-container">
      <Header onNavigate={onNavigate} showHomeButton={true} />
      <div className="setup-header">
        <h1 className="setup-title">🧩 Missing Letters Setup</h1>
        <p className="setup-description">
          Let's create a fun missing letters quiz just for you!
        </p>
      </div>

      {error && (
        <div className="error-message">
          ⚠️ {error}
        </div>
      )}

      <div className="setup-form">
        <div className="form-row">
          <div className="form-group">
            <label>Age <span style={{ fontWeight: 'normal', fontSize: '0.9em', color: '#888' }}>(optional)</span></label>
            <input
              type="text"
              name="grade"
              value={formData.grade}
              onChange={handleInputChange}
              className="form-input"
              placeholder="Enter age (e.g. 8)"
            />
          </div>

          <div className="form-group">
            <label>Difficulty Level</label>
            <select
              name="difficultyLevel"
              value={formData.difficultyLevel}
              onChange={handleInputChange}
              className="form-select"
            >
              <option value="easy">Easy (10% letters missing)</option>
              <option value="medium">Medium (30% letters missing)</option>
              <option value="hard">Hard (90% letters missing)</option>
            </select>
          </div>
        </div>

        <div className="form-row">
          

          <div className="form-group">
            <label>Number of Words</label>
            <input
              type="number"
              name="numberOfWords"
              value={formData.numberOfWords}
              onChange={handleInputChange}
              className="form-input"
              min="1"
              max="20"
            />
          </div>
        </div>

        <div className="form-group">
          <label>Context / Topic</label>
          <input
            type="text"
            name="context"
            value={formData.context}
            onChange={handleInputChange}
            className="form-input"
            placeholder="e.g., animals, sports, food, computer, etc."
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Language</label>
            <select
              name="language"
              value={formData.language}
              onChange={handleInputChange}
              className="form-select"
            >
              <option value="English">English</option>
              <option value="Tamil">Tamil</option>
              <option value="bilingual">Bilingual (Both)</option>
            </select>
          </div>

          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="hints"
                checked={formData.hints}
                onChange={handleInputChange}
                className="form-checkbox"
              />
              <span>Show Phonetic Hints</span>
            </label>
          </div>
        </div>

        <button
          className="start-btn"
          onClick={startQuiz}
          disabled={loading}
        >
          {loading ? '🔄 Generating Words...' : '🚀 Start Quiz'}
        </button>
      </div>
      <Footer />
    </div>
  );
}

export default MissingLetters;
